import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SeoMeta from '@/components/base/SeoMeta';
import JsonLd from '@/components/base/JsonLd';

export default function Archive() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') ?? 'all';
  const queryParam = searchParams.get('q') ?? '';

  const [query, setQuery] = useState(queryParam);
  const [sort, setSort] = useState<'latest' | 'heat'>('latest');
  const [visible, setVisible] = useState(6);

  useEffect(() => {
    setQuery(queryParam);
  }, [queryParam]);

  const setCategory = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'all') {
      next.delete('category');
    } else {
      next.set('category', key);
    }
    setSearchParams(next, { replace: true });
    setVisible(6);
  };

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    const list = articles.filter((item) => {
      const matchCategory = categoryParam === 'all' || item.category === categoryParam;
      const matchKeyword =
        !keyword ||
        item.title.toLowerCase().includes(keyword) ||
        item.summary.toLowerCase().includes(keyword) ||
        item.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCategory && matchKeyword;
    });
    return list.sort((a, b) => {
      if (sort === 'heat') return b.heat - a.heat;
      return a.publishDate < b.publishDate ? 1 : -1;
    });
  }, [categoryParam, query, sort]);

  const shown = filtered.slice(0, visible);
  const activeCategory = categories.find((item) => item.key === categoryParam);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const basePath = (__BASE_PATH__ || '/').replace(/\/$/, '');

  return (
    <>
      <SeoMeta
        title="反转档案｜爆料反转与辟谣澄清案例合集 - 真相回旋镖"
        description="真相回旋镖反转档案收录全部爆料反转、辟谣澄清、真相后续与打脸现场案例，支持按走向分类筛选与关键词搜索，每条案例均附完整时间线。"
        keywords="反转档案,爆料反转案例,辟谣澄清合集,黑料后续,舆情案例"
        path="/archive"
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: '反转档案',
          url: `${origin}${basePath}/archive`,
          description: '收录爆料反转、辟谣澄清与真相后续的案例合集。',
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: filtered.slice(0, 8).map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              url: `${origin}${basePath}/article/${item.id}`,
              name: item.title,
            })),
          },
        }}
      />

      <section className="border-b border-background-300 bg-background-100">
        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '反转档案' }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            {activeCategory ? activeCategory.name : '反转档案'}
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {activeCategory
              ? activeCategory.desc
              : '这里汇总了全部案例，包括已经反转的剧情、得到澄清的误会，以及仍在核实中的争议。每条都标注了核实状态与信息来源。'}
          </p>
        </div>
      </section>

      <section className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCategory('all')}
              className={`cursor-pointer rounded-full border px-3.5 py-1.5 text-sm font-label whitespace-nowrap transition-colors ${
                categoryParam === 'all'
                  ? 'border-primary-500 bg-primary-500 text-background-50'
                  : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
              }`}
            >
              全部
            </button>
            {categories.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setCategory(item.key)}
                className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-label whitespace-nowrap transition-colors ${
                  categoryParam === item.key
                    ? 'border-primary-500 bg-primary-500 text-background-50'
                    : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
                }`}
              >
                <i className={item.icon} aria-hidden="true" />
                {item.name}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative">
              <i
                className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                aria-hidden="true"
              />
              <input
                type="search"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setVisible(6);
                }}
                placeholder="搜索关键词，如 音频鉴定"
                aria-label="搜索案例"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 sm:w-64"
              />
            </div>
            <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
              <button
                type="button"
                onClick={() => setSort('latest')}
                className={`cursor-pointer rounded-full px-3 py-1.5 text-xs font-label transition-colors ${
                  sort === 'latest' ? 'bg-secondary-500 text-background-50' : 'text-foreground-600'
                }`}
              >
                最新
              </button>
              <button
                type="button"
                onClick={() => setSort('heat')}
                className={`cursor-pointer rounded-full px-3 py-1.5 text-xs font-label transition-colors ${
                  sort === 'heat' ? 'bg-secondary-500 text-background-50' : 'text-foreground-600'
                }`}
              >
                最热
              </button>
            </div>
          </div>
        </div>

        <p className="mt-6 text-xs font-label text-foreground-500">
          共找到 {filtered.length} 条案例
        </p>

        {shown.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((item) => (
              <div key={item.id} className="reveal">
                <ArticleCard article={item} />
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-400 bg-background-50 py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-search-eye-line" aria-hidden="true" />
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">没有找到匹配的案例</h2>
            <p className="mt-2 max-w-sm text-sm text-foreground-600">
              试试更换关键词，或者选择“全部”分类重新查看。
            </p>
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setCategory('all');
              }}
              className="mt-6 cursor-pointer rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        )}

        {visible < filtered.length ? (
          <div className="mt-10 flex justify-center">
            <button
              type="button"
              onClick={() => setVisible((prev) => prev + 6)}
              className="flex cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-label text-foreground-800 whitespace-nowrap transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-add-line" aria-hidden="true" />
              加载更多案例
            </button>
          </div>
        ) : null}
      </section>
    </>
  );
}